import { useEffect, useCallback, useMemo, useRef, useState } from "react";
import { ChatHistoryData } from "../types/sidekick";
import { useDispatch, useSelector } from "react-redux";
import {
    fetchChatHistory,
    selectChatHistory
} from "../features/sidekick/sidekickSlice";
import { useDataPagination, UseDataPaginationResult } from "./useDataPagination";

export interface UseChatHistoryResult extends UseDataPaginationResult<ChatHistoryData> {
    chatHistoryData: ChatHistoryData[];
    sortField: string;
    sortDirection: 'asc' | 'desc';
    handleSort: (field: string) => void;
    from: string;
    to: string;
    scrollKey: number;
    hasActiveFilters: boolean;
}

/**
 * Helper function to get effective dates from filterParams or fall back to default dates
 */
const getEffectiveDates = (
    filterParams: Record<string, string | number | string[]>,
    defaultFrom: string,
    defaultTo: string
): { effectiveFrom: string | undefined; effectiveTo: string | undefined } => {
    const effectiveFrom = (typeof filterParams['from'] === 'string' ? filterParams['from'] : defaultFrom) as string | undefined;
    const effectiveTo = (typeof filterParams['to'] === 'string' ? filterParams['to'] : defaultTo) as string | undefined;
    return { effectiveFrom, effectiveTo };
};

export const useChatHistory = (): UseChatHistoryResult => {
    const dispatch = useDispatch<any>();
    const chatHistoryState = useSelector(selectChatHistory);
    const hasFetchedRef = useRef(false);
    const [scrollKey, setScrollKey] = useState(0);
    const isInitialFilterRender = useRef(true);

    // Increment scrollKey when filter params change (skip initial mount)
    useEffect(() => {
        if (isInitialFilterRender.current) {
            isInitialFilterRender.current = false;
            return;
        }
        setScrollKey(k => k + 1);
    }, [chatHistoryState.filterParams]);

    // Callbacks for Redux pagination actions
    const handlePageChange = useCallback((page: number) => {
        if (page === chatHistoryState.currentPage) {
            return;
        }

        const { rowsPerPage, from, to, filterParams } = chatHistoryState;
        const { effectiveFrom, effectiveTo } = getEffectiveDates(filterParams, from, to);
        dispatch(fetchChatHistory({
            page,
            pageSize: rowsPerPage,
            sortBy: chatHistoryState.sortField,
            sortDirection: chatHistoryState.sortDirection,
            from: effectiveFrom,
            to: effectiveTo,
            filters: filterParams,
            requestSource: 'pagination'
        })).then((resultAction: any) => {
            if (fetchChatHistory.fulfilled.match(resultAction)) {
                setScrollKey(k => k + 1);
            }
        });
    }, [dispatch, chatHistoryState.currentPage, chatHistoryState.rowsPerPage, chatHistoryState.sortField, chatHistoryState.sortDirection, chatHistoryState.from, chatHistoryState.to, chatHistoryState.filterParams]);

    const handleRowsPerPageChange = useCallback((rowsPerPage: number) => {
        if (rowsPerPage === chatHistoryState.rowsPerPage) {
            return;
        }

        const { from, to, filterParams } = chatHistoryState;
        const { effectiveFrom, effectiveTo } = getEffectiveDates(filterParams, from, to);
        dispatch(fetchChatHistory({
            page: 1,
            pageSize: rowsPerPage,
            sortBy: chatHistoryState.sortField,
            sortDirection: chatHistoryState.sortDirection,
            from: effectiveFrom,
            to: effectiveTo,
            filters: filterParams,
            requestSource: 'pagination'
        }));
    }, [dispatch, chatHistoryState.rowsPerPage, chatHistoryState.sortField, chatHistoryState.sortDirection, chatHistoryState.from, chatHistoryState.to, chatHistoryState.filterParams]);

    // Handle sorting
    const handleSort = useCallback((field: string) => {
        if (!field) {
            return;
        }

        // If clicking the same field, toggle direction
        const newDirection =
            field === chatHistoryState.sortField && chatHistoryState.sortDirection === 'asc'
                ? 'desc'
                : 'asc';

        // Fetch data with new sort parameters
        const { currentPage, rowsPerPage, from, to, filterParams } = chatHistoryState;
        const { effectiveFrom, effectiveTo } = getEffectiveDates(filterParams, from, to);
        dispatch(fetchChatHistory({
            page: currentPage,
            pageSize: rowsPerPage,
            sortBy: field,
            sortDirection: newDirection,
            from: effectiveFrom,
            to: effectiveTo,
            filters: filterParams,
            requestSource: 'sorting'
        })).then((resultAction: any) => {
            if (fetchChatHistory.fulfilled.match(resultAction)) {
                setScrollKey(k => k + 1);
            }
        });
    }, [dispatch, chatHistoryState.currentPage, chatHistoryState.rowsPerPage, chatHistoryState.sortField, chatHistoryState.sortDirection, chatHistoryState.from, chatHistoryState.to, chatHistoryState.filterParams]);

    // Use the pagination hook with Redux state
    const paginationProps = useDataPagination({
        data: chatHistoryState.data,
        totalCount: chatHistoryState.totalCount,
        loading: chatHistoryState.loading,
        error: chatHistoryState.error,
        currentPage: chatHistoryState.currentPage,
        rowsPerPage: chatHistoryState.rowsPerPage,
        onPageChange: handlePageChange,
        onRowsPerPageChange: handleRowsPerPageChange
    });

    // Fetch chat history data on initial mount only (once per app lifecycle)
    useEffect(() => {
        if (!hasFetchedRef.current && chatHistoryState.data.length === 0 && !chatHistoryState.loading && !chatHistoryState.error) {
            hasFetchedRef.current = true;
            const { currentPage, rowsPerPage, sortField, sortDirection, from, to, filterParams } = chatHistoryState;
            const { effectiveFrom, effectiveTo } = getEffectiveDates(filterParams, from, to);
            dispatch(fetchChatHistory({
                page: currentPage,
                pageSize: rowsPerPage,
                sortBy: sortField,
                sortDirection: sortDirection,
                from: effectiveFrom,
                to: effectiveTo,
                filters: filterParams,
                requestSource: 'initial'
            }));
        }
    }, [dispatch]);

    // Check if there are active filters (beyond default date range)
    const hasActiveFilters = useMemo(() => {
        if (!chatHistoryState.filterParams) return false;

        return Object.values(chatHistoryState.filterParams).some(value => {
            // Explicitly check for null or undefined
            if (value === null || value === undefined) return false;

            // Filter out empty strings (but keep other falsy values like 0 or false)
            if (typeof value === 'string' && value.trim() === '') return false;

            // Filter out empty arrays
            if (Array.isArray(value) && value.length === 0) return false;

            // All other values (including 0, false) are valid filters
            return true;
        });
    }, [chatHistoryState.filterParams]);

    // Memoize the return value to prevent unnecessary re-renders
    return useMemo(() => ({
        ...paginationProps,
        chatHistoryData: chatHistoryState.data,
        sortField: chatHistoryState.sortField,
        sortDirection: chatHistoryState.sortDirection,
        from: chatHistoryState.from,
        to: chatHistoryState.to,
        scrollKey,
        hasActiveFilters,
        handleSort
    }), [
        paginationProps,
        chatHistoryState.data,
        chatHistoryState.sortField,
        chatHistoryState.sortDirection,
        chatHistoryState.from,
        chatHistoryState.to,
        scrollKey,
        hasActiveFilters,
        handleSort
    ]);
};